import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { 
  ClipboardCheck, Plus, Calendar, Clock, FileText, Users, 
  Download, Eye, Edit, Trash2, Upload, CheckCircle, AlertCircle,
  Lock, Unlock, Award, Send, Filter, Search, RefreshCcw,
  ChevronRight, FileUp, Star, XCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Resolve relative upload paths to an absolute backend URL (avoid /api/api). */
const resolveUploadUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const path = url.startsWith('/') ? url : `/${url}`;
  const withApi = path.startsWith('/api/') ? path : `/api${path}`;
  return `${API_URL}${withApi}`;
};

const TrainerEvaluationsPage = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const { lastMessage } = useWebSocket();
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showGradeDialog, setShowGradeDialog] = useState(false);
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  
  // Create form
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    course_id: '',
    evaluation_type: 'exam',
    scheduled_date: '',
    end_date: '',
    duration_minutes: 60,
    max_score: 100,
    passing_score: 50,
    allow_file_submission: true,
    is_published: false
  });
  const [examFile, setExamFile] = useState(null);
  const [creating, setCreating] = useState(false);
  
  // Grading form
  const [gradeData, setGradeData] = useState({
    score: '',
    feedback: ''
  });
  const [grading, setGrading] = useState(false);

  const fetchEvaluations = useCallback(async () => {
    try {
      const response = await api.get('/evaluations');
      setEvaluations(response.data);
    } catch (error) {
      console.error('Error fetching evaluations:', error);
      toast.error('Erreur lors du chargement des évaluations');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCourses = useCallback(async () => {
    try {
      const response = await api.get('/courses');
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  }, []);

  useEffect(() => {
    fetchEvaluations();
    fetchCourses();
  }, [fetchEvaluations, fetchCourses]);

  // WebSocket refresh
  useEffect(() => {
    if (lastMessage?.type === 'data_refresh' && lastMessage?.event_type?.includes('evaluation')) {
      fetchEvaluations();
    }
  }, [lastMessage, fetchEvaluations]);

  const handleCreateEvaluation = async () => {
    if (courses.length === 0) {
      toast.error("Aucun cours disponible. Veuillez d'abord créer un cours avant de créer une évaluation.");
      return;
    }
    if (!formData.title || !formData.course_id) {
      toast.error('Veuillez remplir le titre et sélectionner un cours');
      return;
    }

    // Date coherence: opening date must be strictly before closing date
    if (formData.scheduled_date && formData.end_date && formData.end_date <= formData.scheduled_date) {
      toast.error("La date d'ouverture doit être antérieure à la date de fermeture.");
      return;
    }

    const maxScore = Number(formData.max_score);
    const passingScore = Number(formData.passing_score);
    if (Number.isNaN(maxScore) || maxScore <= 0) {
      toast.error('La note maximale doit être supérieure à 0');
      return;
    }
    if (Number.isNaN(passingScore) || passingScore < 0) {
      toast.error('La note de passage est invalide');
      return;
    }
    if (passingScore > maxScore) {
      toast.error('La note de passage ne peut pas être supérieure à la note maximale');
      return;
    }

    setCreating(true);
    try {
      // Create evaluation
      const response = await api.post('/evaluations', {
        ...formData,
        course_id: parseInt(formData.course_id),
        max_score: maxScore,
        passing_score: passingScore,
      });

      const evaluationId = response.data.id;

      // Upload exam document if provided
      if (examFile) {
        const fileFormData = new FormData();
        fileFormData.append('file', examFile);
        await api.post(`/evaluations/${evaluationId}/upload-exam`, fileFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      toast.success('Évaluation créée avec succès');
      setShowCreateDialog(false);
      resetForm();
      fetchEvaluations();
    } catch (error) {
      console.error('Error creating evaluation:', error);
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string'
        ? detail
        : (Array.isArray(detail) ? detail.map((e) => e.msg || e).join(', ') : null);
      toast.error(message || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const handlePublish = async (evaluation) => {
    try {
      await api.put(`/evaluations/${evaluation.id}`, {
        is_published: !evaluation.is_published
      });
      toast.success(evaluation.is_published ? 'Évaluation dépubliée' : 'Évaluation publiée');
      fetchEvaluations();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (evaluationId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette évaluation ?')) return;
    
    try {
      await api.delete(`/evaluations/${evaluationId}`);
      toast.success('Évaluation supprimée');
      fetchEvaluations();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleViewDetail = async (evaluation) => {
    try {
      const response = await api.get(`/evaluations/${evaluation.id}`);
      setSelectedEvaluation(response.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error('Erreur lors du chargement des détails');
    }
  };

  const handleGradeSubmission = async () => {
    if (!gradeData.score) {
      toast.error('Veuillez entrer une note');
      return;
    }

    setGrading(true);
    try {
      await api.put(`/evaluations/submissions/${selectedSubmission.id}/grade`, {
        score: parseFloat(gradeData.score),
        feedback: gradeData.feedback
      });
      toast.success('Note enregistrée');
      setShowGradeDialog(false);
      setGradeData({ score: '', feedback: '' });
      handleViewDetail(selectedEvaluation);
    } catch (error) {
      toast.error('Erreur lors de la notation');
    } finally {
      setGrading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      course_id: '',
      evaluation_type: 'exam',
      scheduled_date: '',
      end_date: '',
      duration_minutes: 60,
      max_score: 100,
      passing_score: 50,
      allow_file_submission: true,
      is_published: false
    });
    setExamFile(null);
  };

  const getStatusBadge = (status) => {
    const badges = {
      upcoming: { label: 'À venir', class: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
      open: { label: 'En cours', class: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
      closed: { label: 'Terminée', class: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300' },
      available: { label: 'Disponible', class: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400' }
    };
    return badges[status] || badges.available;
  };

  const filteredEvaluations = evaluations.filter(evaluation => {
    const matchesSearch = evaluation.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTab = activeTab === 'all' || 
      (activeTab === 'upcoming' && evaluation.status === 'upcoming') ||
      (activeTab === 'open' && evaluation.status === 'open') ||
      (activeTab === 'closed' && evaluation.status === 'closed') ||
      (activeTab === 'draft' && !evaluation.is_published);
    return matchesSearch && matchesTab;
  });

  // Stats
  const stats = {
    total: evaluations.length,
    upcoming: evaluations.filter(e => e.status === 'upcoming').length,
    open: evaluations.filter(e => e.status === 'open').length,
    toGrade: evaluations.reduce((sum, e) => sum + (e.submissions?.filter(s => s.status === 'submitted').length || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header title="Gestion des Évaluations" onMenuClick={onMenuClick} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-violet-500 to-purple-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-80">Total</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <ClipboardCheck className="w-8 h-8 opacity-80" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-500 to-cyan-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-80">À venir</p>
                  <p className="text-2xl font-bold">{stats.upcoming}</p>
                </div>
                <Calendar className="w-8 h-8 opacity-80" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-80">En cours</p>
                  <p className="text-2xl font-bold">{stats.open}</p>
                </div>
                <Unlock className="w-8 h-8 opacity-80" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-500 to-orange-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-80">À noter</p>
                  <p className="text-2xl font-bold">{stats.toGrade}</p>
                </div>
                <Award className="w-8 h-8 opacity-80" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Header Actions */}
        <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center mb-6">
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-64"
                data-testid="search-evaluations"
              />
            </div>
            <Button variant="outline" size="icon" onClick={fetchEvaluations}>
              <RefreshCcw className="w-4 h-4" />
            </Button>
          </div>
          
          <Button 
            onClick={() => setShowCreateDialog(true)}
            className="bg-violet-600 hover:bg-violet-700"
            data-testid="create-evaluation-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle Évaluation
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="bg-white dark:bg-slate-800 border">
            <TabsTrigger value="all" data-testid="tab-all">Toutes</TabsTrigger>
            <TabsTrigger value="upcoming" data-testid="tab-upcoming">À venir</TabsTrigger>
            <TabsTrigger value="open" data-testid="tab-open">En cours</TabsTrigger>
            <TabsTrigger value="closed" data-testid="tab-closed">Terminées</TabsTrigger>
            <TabsTrigger value="draft" data-testid="tab-draft">Brouillons</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Evaluations List */}
        {filteredEvaluations.length > 0 ? (
          <div className="space-y-4">
            {filteredEvaluations.map((evaluation) => {
              const statusBadge = getStatusBadge(evaluation.status);
              return (
                <Card 
                  key={evaluation.id} 
                  className="hover:shadow-lg transition-shadow"
                  data-testid={`evaluation-card-${evaluation.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                            {evaluation.title}
                          </h3>
                          <Badge className={statusBadge.class}>
                            {statusBadge.label}
                          </Badge>
                          {!evaluation.is_published && (
                            <Badge variant="outline" className="border-amber-500 text-amber-600">
                              Brouillon
                            </Badge>
                          )}
                        </div>
                        
                        <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                          {evaluation.description || 'Aucune description'}
                        </p>
                        
                        <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                          {evaluation.course_name && (
                            <span className="flex items-center gap-1">
                              <FileText className="w-4 h-4" />
                              {evaluation.course_name}
                            </span>
                          )}
                          {evaluation.scheduled_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              Ouverture: {new Date(evaluation.scheduled_date).toLocaleDateString('fr-FR', { 
                                day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' 
                              })}
                            </span>
                          )}
                          {evaluation.end_date && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              Fermeture: {new Date(evaluation.end_date).toLocaleDateString('fr-FR', { 
                                day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' 
                              })}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {evaluation.submission_count || 0} soumission(s)
                          </span>
                          {evaluation.exam_document_url && (
                            <span className="flex items-center gap-1 text-emerald-600">
                              <FileUp className="w-4 h-4" />
                              Document joint
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleViewDetail(evaluation)}
                          data-testid={`view-evaluation-${evaluation.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Détails
                        </Button>
                        <Button 
                          variant={evaluation.is_published ? "outline" : "default"}
                          size="sm"
                          onClick={() => handlePublish(evaluation)}
                          className={evaluation.is_published ? "" : "bg-emerald-600 hover:bg-emerald-700"}
                          data-testid={`publish-evaluation-${evaluation.id}`}
                        >
                          {evaluation.is_published ? (
                            <>
                              <XCircle className="w-4 h-4 mr-1" />
                              Dépublier
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-1" />
                              Publier
                            </>
                          )}
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDelete(evaluation.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-evaluation-${evaluation.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <ClipboardCheck className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                {searchTerm ? 'Aucune évaluation trouvée' : 'Aucune évaluation créée'}
              </p>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Créer une évaluation
              </Button>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Create Evaluation Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-violet-600" />
              Nouvelle Évaluation
            </DialogTitle>
            <DialogDescription>
              Créez une nouvelle évaluation avec document d'examen et planification
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Titre *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Ex: Examen final JavaScript"
                  data-testid="create-eval-title"
                />
              </div>
              
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Instructions pour les étudiants..."
                  rows={2}
                  data-testid="create-eval-description"
                />
              </div>
              
              <div>
                <Label>Cours *</Label>
                <Select 
                  value={formData.course_id} 
                  onValueChange={(v) => setFormData({ ...formData, course_id: v })}
                >
                  <SelectTrigger data-testid="create-eval-course">
                    <SelectValue placeholder="Sélectionner un cours" />
                  </SelectTrigger>
                  <SelectContent>
                    {courses.map(course => (
                      <SelectItem key={course.id} value={course.id.toString()}>
                        {course.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Type</Label>
                <Select 
                  value={formData.evaluation_type} 
                  onValueChange={(v) => setFormData({ ...formData, evaluation_type: v })}
                >
                  <SelectTrigger data-testid="create-eval-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="exam">Examen</SelectItem>
                    <SelectItem value="quiz">Quiz</SelectItem>
                    <SelectItem value="assignment">Devoir noté</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Date d'ouverture</Label>
                <Input
                  type="datetime-local"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                  data-testid="create-eval-scheduled"
                />
                <p className="text-xs text-slate-500 mt-1">
                  L'évaluation sera verrouillée jusqu'à cette date
                </p>
              </div>
              
              <div>
                <Label>Date de fermeture</Label>
                <Input
                  type="datetime-local"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  data-testid="create-eval-end"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Aucune soumission possible après cette date
                </p>
              </div>
              
              <div>
                <Label>Durée (minutes)</Label>
                <Input
                  type="number"
                  min="5"
                  max="480"
                  value={formData.duration_minutes}
                  onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
                  data-testid="create-eval-duration"
                />
              </div>
              
              <div>
                <Label>Note maximale</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.max_score}
                  onChange={(e) => setFormData({ ...formData, max_score: parseFloat(e.target.value) })}
                  data-testid="create-eval-max-score"
                />
              </div>
              
              <div>
                <Label>Note de passage</Label>
                <Input
                  type="number"
                  min="0"
                  max={formData.max_score}
                  value={formData.passing_score}
                  onChange={(e) => setFormData({ ...formData, passing_score: parseFloat(e.target.value) })}
                  data-testid="create-eval-passing"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Doit être inférieure ou égale à la note maximale
                </p>
              </div>
              
              <div>
                <Label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.allow_file_submission}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      // Turning off file upload also clears any staged exam document
                      setFormData({ ...formData, allow_file_submission: checked });
                      if (!checked) setExamFile(null);
                    }}
                    className="w-4 h-4"
                  />
                  Autoriser l'upload de fichiers
                </Label>
              </div>
              
              {formData.allow_file_submission && (
                <div className="col-span-2">
                  <Label>Document d'examen (PDF, Word...)</Label>
                  <div className="mt-2 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg p-4 text-center">
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.odt,.rtf"
                      onChange={(e) => setExamFile(e.target.files?.[0] || null)}
                      className="hidden"
                      id="exam-file-upload"
                      data-testid="create-eval-file"
                    />
                    <label htmlFor="exam-file-upload" className="cursor-pointer">
                      {examFile ? (
                        <div className="flex items-center justify-center gap-2 text-emerald-600">
                          <FileUp className="w-5 h-5" />
                          {examFile.name}
                        </div>
                      ) : (
                        <div className="text-slate-500">
                          <Upload className="w-8 h-8 mx-auto mb-2" />
                          <p>Cliquez pour ajouter un document</p>
                          <p className="text-xs">PDF, Word, ODT (max 50MB)</p>
                        </div>
                      )}
                    </label>
                  </div>
                </div>
              )}
              
              <div className="col-span-2">
                <Label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_published}
                    onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                    className="w-4 h-4"
                  />
                  Publier immédiatement (les étudiants seront notifiés)
                </Label>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
              Annuler
            </Button>
            <Button 
              onClick={handleCreateEvaluation} 
              disabled={creating}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="submit-create-eval"
            >
              {creating ? 'Création...' : 'Créer l\'évaluation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Evaluation Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-violet-600" />
              {selectedEvaluation?.title}
            </DialogTitle>
            <DialogDescription>
              Détails de l'évaluation et gestion des soumissions
            </DialogDescription>
          </DialogHeader>
          
          {selectedEvaluation && (
            <div className="space-y-6 py-4">
              {/* Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-xs text-slate-500">Statut</p>
                  <Badge className={getStatusBadge(selectedEvaluation.status).class}>
                    {getStatusBadge(selectedEvaluation.status).label}
                  </Badge>
                </div>
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-xs text-slate-500">Note max</p>
                  <p className="font-bold">{selectedEvaluation.max_score} pts</p>
                </div>
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-xs text-slate-500">Note de passage</p>
                  <p className="font-bold">{selectedEvaluation.passing_score} pts</p>
                </div>
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-xs text-slate-500">Soumissions</p>
                  <p className="font-bold">{selectedEvaluation.submissions?.length || 0}</p>
                </div>
              </div>

              {/* Exam document */}
              {selectedEvaluation.exam_document_url && (
                <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-emerald-600" />
                    <span className="font-medium">
                      {selectedEvaluation.exam_document_name || 'Document d\'examen'}
                    </span>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const fullUrl = resolveUploadUrl(selectedEvaluation.exam_document_url);
                      if (fullUrl) window.open(fullUrl, '_blank', 'noopener,noreferrer');
                    }}
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Télécharger
                  </Button>
                </div>
              )}

              {/* Submissions */}
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Soumissions des étudiants
                </h4>
                
                {selectedEvaluation.submissions?.length > 0 ? (
                  <div className="space-y-3">
                    {selectedEvaluation.submissions.map((submission) => (
                      <div 
                        key={submission.id}
                        className="p-4 border rounded-lg flex items-center justify-between"
                        data-testid={`submission-${submission.id}`}
                      >
                        <div>
                          <p className="font-medium">{submission.student_name}</p>
                          <p className="text-sm text-slate-500">
                            Soumis le {new Date(submission.submitted_at).toLocaleDateString('fr-FR', {
                              day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                            })}
                          </p>
                          {submission.score !== null && (
                            <div className="flex items-center gap-2 mt-1">
                              <Badge className={submission.score >= selectedEvaluation.passing_score 
                                ? 'bg-emerald-100 text-emerald-700' 
                                : 'bg-red-100 text-red-700'
                              }>
                                {submission.score} / {selectedEvaluation.max_score}
                              </Badge>
                              {submission.score >= selectedEvaluation.passing_score ? (
                                <CheckCircle className="w-4 h-4 text-emerald-600" />
                              ) : (
                                <AlertCircle className="w-4 h-4 text-red-600" />
                              )}
                            </div>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {submission.submission_file_url && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                const fullUrl = resolveUploadUrl(submission.submission_file_url);
                                if (fullUrl) window.open(fullUrl, '_blank', 'noopener,noreferrer');
                              }}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Télécharger
                            </Button>
                          )}
                          {submission.status === 'submitted' ? (
                            <Button 
                              size="sm"
                              className="bg-amber-600 hover:bg-amber-700"
                              onClick={() => {
                                setSelectedSubmission(submission);
                                setShowGradeDialog(true);
                              }}
                              data-testid={`grade-submission-${submission.id}`}
                            >
                              <Award className="w-4 h-4 mr-1" />
                              Noter
                            </Button>
                          ) : (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                setSelectedSubmission(submission);
                                setGradeData({ score: submission.score?.toString() || '', feedback: submission.feedback || '' });
                                setShowGradeDialog(true);
                              }}
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Modifier
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>Aucune soumission pour le moment</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Grade Dialog */}
      <Dialog open={showGradeDialog} onOpenChange={setShowGradeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-600" />
              Noter la soumission
            </DialogTitle>
            <DialogDescription>
              Étudiant: {selectedSubmission?.student_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Note (sur {selectedEvaluation?.max_score})</Label>
              <Input
                type="number"
                min="0"
                max={selectedEvaluation?.max_score}
                value={gradeData.score}
                onChange={(e) => setGradeData({ ...gradeData, score: e.target.value })}
                placeholder="Ex: 75"
                data-testid="grade-score-input"
              />
            </div>
            
            <div>
              <Label>Commentaire / Feedback</Label>
              <Textarea
                value={gradeData.feedback}
                onChange={(e) => setGradeData({ ...gradeData, feedback: e.target.value })}
                placeholder="Commentaires pour l'étudiant..."
                rows={4}
                data-testid="grade-feedback-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGradeDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleGradeSubmission}
              disabled={grading}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="submit-grade"
            >
              {grading ? 'Enregistrement...' : 'Enregistrer la note'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TrainerEvaluationsPage;
